import { defineStore } from 'pinia';
import employeeApi from '@/api/employeeApi.js';

export const useRbacStore = defineStore('rbacStore', {
    state: () => ({
        departments: [],
        departmentsTree: [],
        positions: [],
        permissionsCache: {}, // ключ: `${entityType}_${entityId}`, значение: permissions data
        loading: {
            departments: false,
            positions: false,
            permissions: false,
        },
    }),

    getters: {
        // Получить отдел по ID
        getDepartmentById: (state) => (id) => {
            return state.departments.find(d => d.id === id);
        },

        // Получить должность по ID
        getPositionById: (state) => (id) => {
            return state.positions.find(p => p.id === id);
        },

        // Получить должности по отделу
        getPositionsByDepartment: (state) => (departmentId) => {
            if (!departmentId) return state.positions;
            return state.positions.filter(p => p.department === departmentId);
        },

        // Получить кешированные права
        getCachedPermissions: (state) => (entityType, entityId) => {
            const key = `${entityType}_${entityId}`;
            return state.permissionsCache[key] || null;
        },
    },

    actions: {
        // === Departments ===
        async loadDepartments() {
            this.loading.departments = true;
            try {
                const data = await employeeApi.getDepartments();
                this.departments = Array.isArray(data) ? data : [];
                this._buildDepartmentsTree();
            } catch (error) {
                console.error('Ошибка загрузки отделов:', error);
                this.departments = [];
                this.departmentsTree = [];
            } finally {
                this.loading.departments = false;
            }
        },

        async loadDepartment(id) {
            try {
                const data = await employeeApi.getDepartment(id);
                // Обновляем в списке, если уже есть
                const index = this.departments.findIndex(d => d.id === id);
                if (index !== -1) {
                    this.departments[index] = data;
                } else {
                    this.departments.push(data);
                }
                this._buildDepartmentsTree();
                return data;
            } catch (error) {
                console.error(`Ошибка загрузки отдела ${id}:`, error);
                throw error;
            }
        },

        async createDepartment(departmentData) {
            try {
                const newDept = await employeeApi.createDepartment(departmentData);
                this.departments.push(newDept);
                this._buildDepartmentsTree();
                return newDept;
            } catch (error) {
                console.error('Ошибка создания отдела:', error);
                throw error;
            }
        },

        async updateDepartment(id, departmentData) {
            try {
                const updated = await employeeApi.updateDepartment(id, departmentData);
                const index = this.departments.findIndex(d => d.id === id);
                if (index !== -1) {
                    this.departments[index] = updated;
                }
                this._buildDepartmentsTree();
                return updated;
            } catch (error) {
                console.error(`Ошибка обновления отдела ${id}:`, error);
                throw error;
            }
        },

        async deleteDepartment(id) {
            try {
                await employeeApi.deleteDepartment(id);
                this.departments = this.departments.filter(d => d.id !== id);
                this._buildDepartmentsTree();
            } catch (error) {
                console.error(`Ошибка удаления отдела ${id}:`, error);
                throw error;
            }
        },

        // Построение дерева отделов для TreeTable/TreeSelect
        _buildDepartmentsTree() {
            const map = {};
            const roots = [];

            // Создаём мапу всех отделов
            this.departments.forEach(dept => {
                map[dept.id] = { ...dept, children: [] };
            });

            // Строим дерево
            this.departments.forEach(dept => {
                if (dept.parent) {
                    const parent = map[dept.parent];
                    if (parent) {
                        parent.children.push(map[dept.id]);
                    } else {
                        // Если родитель не найден, добавляем в корень
                        roots.push(map[dept.id]);
                    }
                } else {
                    roots.push(map[dept.id]);
                }
            });

            this.departmentsTree = roots;
        },

        // === Positions ===
        async loadPositions(departmentId = null) {
            this.loading.positions = true;
            try {

                // Загружаем ВСЕ позиции, обходя пагинацию
                let allPositions = [];
                let page = 1;
                let hasMore = true;

                while (hasMore) {
                    const params = departmentId ? { page, department: departmentId } : { page };
                    const data = await employeeApi.getPositions(params);

                    const results = Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []);

                    allPositions = [...allPositions, ...results];

                    // Проверяем есть ли еще страницы
                    hasMore = data?.next != null;
                    page++;
                }

                this.positions = allPositions;
            } catch (error) {
                console.error('Ошибка загрузки должностей:', error);
                this.positions = [];
            } finally {
                this.loading.positions = false;
            }
        },

        async loadPosition(id) {
            try {
                const data = await employeeApi.getPosition(id);
                const index = this.positions.findIndex(p => p.id === id);
                if (index !== -1) {
                    this.positions[index] = data;
                } else {
                    this.positions.push(data);
                }
                return data;
            } catch (error) {
                console.error(`Ошибка загрузки должности ${id}:`, error);
                throw error;
            }
        },

        async createPosition(positionData) {
            try {
                const newPos = await employeeApi.createPosition(positionData);
                this.positions.push(newPos);
                return newPos;
            } catch (error) {
                console.error('Ошибка создания должности:', error);
                throw error;
            }
        },

        async updatePosition(id, positionData) {
            try {
                const updated = await employeeApi.updatePosition(id, positionData);
                const index = this.positions.findIndex(p => p.id === id);
                if (index !== -1) {
                    this.positions[index] = updated;
                }
                return updated;
            } catch (error) {
                console.error(`Ошибка обновления должности ${id}:`, error);
                throw error;
            }
        },

        async deletePosition(id) {
            try {
                await employeeApi.deletePosition(id);
                this.positions = this.positions.filter(p => p.id !== id);
            } catch (error) {
                console.error(`Ошибка удаления должности ${id}:`, error);
                throw error;
            }
        },

        // === Permissions ===
        async loadPermissions(entityType, entityId) {
            this.loading.permissions = true;
            try {
                let data;
                if (entityType === 'department') {
                    data = await employeeApi.getDepartmentPermissions(entityId);
                } else if (entityType === 'position') {
                    data = await employeeApi.getPositionPermissions(entityId);
                } else if (entityType === 'user') {
                    data = await employeeApi.getUserPermissions(entityId);
                } else {
                    throw new Error(`Unknown entity type: ${entityType}`);
                }

                const key = `${entityType}_${entityId}`;
                this.permissionsCache[key] = data;
                return data;
            } catch (error) {
                console.error(`Ошибка загрузки прав для ${entityType} ${entityId}:`, error);
                throw error;
            } finally {
                this.loading.permissions = false;
            }
        },

        async updatePermissions(entityType, entityId, permissions) {
            try {
                let data;
                if (entityType === 'department') {
                    data = await employeeApi.updateDepartmentPermissions(entityId, permissions);
                } else if (entityType === 'position') {
                    data = await employeeApi.updatePositionPermissions(entityId, permissions);
                } else {
                    throw new Error(`Unknown entity type: ${entityType}`);
                }

                const key = `${entityType}_${entityId}`;
                this.permissionsCache[key] = data;
                return data;
            } catch (error) {
                console.error(`Ошибка обновления прав для ${entityType} ${entityId}:`, error);
                throw error;
            }
        },

        async updateUserPermissions(userId, { extra_permissions, revoked_permissions }) {
            try {
                const data = await employeeApi.updateUserPermissions(userId, {
                    extra_permissions,
                    revoked_permissions
                });

                const key = `user_${userId}`;
                this.permissionsCache[key] = data;
                return data;
            } catch (error) {
                console.error(`Ошибка обновления прав пользователя ${userId}:`, error);
                throw error;
            }
        },

        // Очистка кеша прав
        clearPermissionsCache(entityType = null, entityId = null) {
            if (entityType && entityId) {
                const key = `${entityType}_${entityId}`;
                delete this.permissionsCache[key];
            } else {
                this.permissionsCache = {};
            }
        },
    },
});
